import React, {
  ChangeEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import RCDrawer from "rc-drawer";
import { mdiMenuUp, mdiClose, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment-timezone";
import { Reference, useApolloClient, useMutation } from "@apollo/client";
import { NameKeyPair } from "src/common-services";
import _ from "lodash";

import { SubscriptionContext } from "src/services/SubscriptionService";
import { WEB_SPORTS_MAP } from "src/constants";
import { useReactiveVarListener } from "src/services/UtilService";
import { gamesDashboardEvent } from "src/services/ApolloService";
import { CREATE_USER_UI_SETTING, DELETE_USER_UI_SETTING, UPDATE_USER_UI_SETTINGS } from "src/gql/mutations";
import { DEFAULT_DASH_NAME } from "src/pages/[sport]/[...page]";
import AnalyticsService from "src/services/AnalyticsService";
import styles from "./styles.scss";
import { ColumnOrderSetting, PageItem, BookIdSetting } from "src/typings/[sport]/[...page]";

const DEFAULT_DRAWER_WIDTH = 400;

export interface DrawerHandle {
  toggleDrawer(): void;
  closeDrawer(): void;
}

interface DrawerProps {
  sportUrl: string;
  settingList: ColumnOrderSetting[];
  activeBookId: string;
  activeColumnOrder: number[];
  activeDashboardName: string;
  selectedPage: PageItem;
  selectedBetType: NameKeyPair;
  userBookSetting: BookIdSetting;
  userColumnOrderSetting: ColumnOrderSetting;
  onClose?(): void;
  setDashName(name: string | null): void;
  saveSetting(): void;
  updateSetting(): void;
  deleteSetting(): void;
  selectSetting(): void;
}

const Drawer = forwardRef(
  (
    {
      sportUrl,
      settingList,
      activeBookId,
      activeColumnOrder,
      activeDashboardName,
      selectedPage,
      selectedBetType,
      userBookSetting,
      userColumnOrderSetting,
      onClose,
      setDashName,
      saveSetting,
      updateSetting,
      deleteSetting,
      selectSetting,
    }: DrawerProps,
    ref: any
  ) => {
    const ctx = useContext(SubscriptionContext);
    const currentSportObj = WEB_SPORTS_MAP[sportUrl];
    const hasAccess = ctx.hasSubscriptionAccess(currentSportObj.key, true);
    const apolloClient = useApolloClient();
    // State
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [drawerWidth, setDrawerWidth] = useState<string>(`${DEFAULT_DRAWER_WIDTH}px`);
    const [dashboardName, setDashboardName] = useState<string | null>(null);
    // Mutations
    const [createUserUISetting] = useMutation(CREATE_USER_UI_SETTING);
    const [updateUserUISetting] = useMutation(UPDATE_USER_UI_SETTINGS);
    const [deleteUserUISetting] = useMutation(DELETE_USER_UI_SETTING);

    const currentDashName = dashboardName ?? activeDashboardName;
    const isDefaultDashName = currentDashName === DEFAULT_DASH_NAME;

    /** Toggle the drawer open and closed using dynamc screenwidth? */
    const toggleDrawer = () => {
      setDrawerWidth(window.innerWidth < DEFAULT_DRAWER_WIDTH ? `${window.innerWidth}px` : `${DEFAULT_DRAWER_WIDTH}px`);
      setDrawerOpen(!drawerOpen);
      AnalyticsService.track("Toggle Folder Icon", {
        screen: "FeedView",
        page: selectedPage.key,
        sport: sportUrl,
        book: activeBookId,
        dashboardName: currentDashName,
        settings: activeColumnOrder,
        numColumns: activeColumnOrder.length,
      });
    };

    /** Close the drawer */
    const closeDrawer = useCallback(() => {
      setDrawerOpen(false);
      onClose?.();
    }, [onClose]);

    /** Close the drawer and open the signup modal */
    const onOpenSignup = useCallback(() => {
      closeDrawer();
      ctx.openLoginModal(1);
    }, [closeDrawer, ctx.openLoginModal]);

    /** Close the drawer and open the login modal */
    const onOpenLogin = useCallback(() => {
      closeDrawer();
      ctx.openLoginModal(0);
    }, [closeDrawer, ctx.openLoginModal]);

    /** Save the dashboard name in the drawer as the user types */
    const setNewDashboardName: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        setDashName(e.target.value);
        setDashboardName(null);
        AnalyticsService.track("Save Dashboard Name - Input Textbox", {
          screen: "FeedView",
          page: selectedPage.key,
          sport: sportUrl,
          dashboardName: e.target.value,
          settings: activeColumnOrder,
          book: activeBookId,
          numColumns: activeColumnOrder.length,
        });
      },
      [sportUrl, selectedPage.key, activeColumnOrder, activeBookId]
    );

    /** Debounce saving the users dash name until they're done typing */
    const debounceSetDashName = useMemo(() => _.debounce(setNewDashboardName, 300), [setNewDashboardName]);

    /** Set local state immediately, but delay saving externally */
    const onChangeDashName: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        setDashboardName(e.target.value);
        debounceSetDashName(e);
      },
      [debounceSetDashName]
    );

    /** Reset the dashname locally and externally if the user didn't type anything */
    const onBlurDashName = useCallback(() => {
      if (currentDashName === "") {
        setDashboardName(null);
        setDashName(null);
      }
    }, [currentDashName, setDashName]);

    /** Clear the input if it's currently set to the default title */
    const onFocusDashName = useCallback(() => {
      if (isDefaultDashName) {
        setDashboardName("");
      }
    }, [isDefaultDashName]);

    /** Create a new setting for the dashboardName, bookId and columnOrder that the user has selected */
    const onSaveSetting = async () => {
      // Check if they selected a title
      if (activeDashboardName === DEFAULT_DASH_NAME) {
        // TODO show the user an error: Choose a dashboard name
        return;
      }
      // Create the setting
      await createUserUISetting({
        variables: {
          league: currentSportObj.key,
          section: selectedPage.enum,
          settingType: "COLUMNDISPLAYORDER",
          settingValue: JSON.stringify({ name: activeDashboardName, columnOrder: activeColumnOrder }),
        },
        optimisticResponse: {
          createUserUiSetting: {
            __typename: "UserUISetting",
            id: "new-setting",
            parsedValue: {
              name: activeDashboardName,
              columnOrder: activeColumnOrder,
            },
            uiSection: selectedPage.enum,
            updateTs: moment.utc().toISOString(),
          },
        },
        update: (cache, { data: newUserSettingData }) => {
          const newUserSetting = newUserSettingData?.createUserUiSetting;
          if (newUserSetting) {
            cache.modify({
              fields: {
                [`userUiSetting({\"league\":\"${currentSportObj.key}\",\"section\":\"${selectedPage.enum}\",\"settingType\":\"COLUMNDISPLAYORDER\"})`]:
                  (_prevSetting, { toReference }) => toReference(newUserSetting),
                [`userUiSettings({\"league\":\"${currentSportObj.key}\",\"section\":\"${selectedPage.enum}\",\"settingType\":\"COLUMNDISPLAYORDER\"})`]:
                  (prevSettings = [], { toReference }) => [toReference(newUserSetting), ...prevSettings],
              },
            });
          }
        },
      });
      // Save the book if needed
      // TODO the book is not tied to the dashboard, so not sure why we save this at the same time
      await updateBookId();
      // Reset unsaved values
      saveSetting();
      AnalyticsService.track("Save New - Dashboard Button", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: selectedBetType.key,
        sport: sportUrl,
        book: activeBookId,
        dashboardName: currentDashName,
        settings: activeColumnOrder,
        numColumns: activeColumnOrder.length,
      });
    };

    /** Update the bookId and columnOrder settings for the currently selected dash if needed */
    const onUpdateSettings = async () => {
      // No need to update if the name or column order hasn't changed
      if (
        userColumnOrderSetting.parsedValue.name !== activeDashboardName ||
        JSON.stringify(userColumnOrderSetting.parsedValue.columnOrder) !== JSON.stringify(activeColumnOrder)
      ) {
        const newSettingValue = {
          name: activeDashboardName,
          columnOrder: activeColumnOrder,
        };
        await updateUserUISetting({
          variables: {
            settingId: userColumnOrderSetting.id,
            settingValue: JSON.stringify(newSettingValue),
          },
          optimisticResponse: {
            updateUserUiSettingId: {
              ...userColumnOrderSetting,
              parsedValue: newSettingValue,
              updateTs: moment.utc().toISOString(),
            },
          },
        });
      }
      // Save the book if needed
      // TODO the book is not tied to the dashboard, so not sure why we save this at the same time
      await updateBookId();
      // Reset unsaved changes
      updateSetting();
      AnalyticsService.track("Update - Dashboard Button", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: selectedBetType.key,
        sport: sportUrl,
        dashboardName: currentDashName,
        book: activeBookId,
        settings: activeColumnOrder,
        numColumns: activeColumnOrder.length,
      });
    };

    /** Helper function for saving the book setting if needed */
    const updateBookId = async () => {
      if (userBookSetting.parsedValue !== activeBookId) {
        await updateUserUISetting({
          variables: {
            settingId: userBookSetting.id,
            settingValue: activeBookId,
          },
          optimisticResponse: {
            updateUserUiSettingId: {
              ...userBookSetting,
              parsedValue: activeBookId,
            },
          },
        });
      }
    };

    /** Delete a setting */
    const onDeleteSetting = async (settingId: string) => {
      await deleteUserUISetting({
        variables: { settingId: settingId },
        optimisticResponse: {
          deleteUserUiSetting: {
            status: true,
          },
        },
        refetchQueries: ["userSettingsAndBooks"],
      });
      deleteSetting();
      AnalyticsService.track("Delete Setting From Right Panel", {
        screen: "FeedView",
        page: selectedPage.key,
        sport: sportUrl,
        settingId: settingId,
      });
    };

    /** Select a saved setting from our list */
    const onSelectSetting = async (setting: ColumnOrderSetting) => {
      const settingValueObject = setting.parsedValue;
      selectSetting();
      if (!setting.updateTs) {
        apolloClient.cache.modify({
          fields: {
            [`userUiSetting({\"league\":\"${currentSportObj.key}\",\"section\":\"${selectedPage.enum}\",\"settingType\":\"COLUMNDISPLAYORDER\"})`]:
              (_prevSetting, { toReference }) =>
                toReference({
                  ...setting,
                  __typename: "UserUISetting",
                }),
          },
        });
      } else {
        await updateUserUISetting({
          variables: { settingId: setting.id },
          optimisticResponse: {
            updateUserUiSettingId: {
              ...setting,
              updateTs: moment.utc().toISOString(),
            },
          },
          update: (cache, { data: newUserSettingData }) => {
            const newUserSetting = newUserSettingData?.updateUserUiSettingId;
            if (newUserSetting) {
              cache.modify({
                fields: {
                  [`userUiSetting({\"league\":\"${currentSportObj.key}\",\"section\":\"${selectedPage.enum}\",\"settingType\":\"COLUMNDISPLAYORDER\"})`]:
                    (_prevSetting, { toReference }) => toReference(newUserSetting),
                  [`userUiSettings({\"league\":\"${currentSportObj.key}\",\"section\":\"${selectedPage.enum}\",\"settingType\":\"COLUMNDISPLAYORDER\"})`]:
                    (prevSettings = [], { toReference }) => {
                      const newRef = toReference(newUserSetting);
                      const tempList = prevSettings.filter((s: Reference) => s.__ref !== newRef?.__ref);
                      return [newRef, ...tempList];
                    },
                },
              });
            }
          },
        });
      }
      closeDrawer();
      AnalyticsService.track("Select Setting from Right Panel", {
        screen: "FeedView",
        page: selectedPage.key,
        sport: sportUrl,
        book: activeBookId,
        dashboardName: settingValueObject.name,
        settings: settingValueObject.columnOrder,
        numColumns: settingValueObject.columnOrder.length,
      });
    };

    /** Watch for this event to determine if we need to fire off a toggle */
    useReactiveVarListener(gamesDashboardEvent, (e) => {
      if (e) {
        toggleDrawer();
      }
    });

    useImperativeHandle(ref, () => ({
      toggleDrawer,
      closeDrawer,
    }));

    return (
      <RCDrawer open={drawerOpen} onClose={closeDrawer} placement="right" width={drawerWidth}>
        <div className="settings-drawer-content">
          <button
            className="settings-drawer-content__setting-close-button"
            type="button"
            aria-label="Close"
            onClick={closeDrawer}
          >
            <Icon path={mdiClose} size="25px" color="#000000" />
          </button>
          <span className="settings-drawer-content__heading-name">{sportUrl.toUpperCase()} Dashboard</span>
          {!ctx.signedIn ? (
            <div className="settings-drawer-content__body">
              <div className="settings-drawer-content__login-view">
                <div className="settings-drawer-content__login-view--login-message">
                  You can create and save your customized dashboards here. As a BetQL member these views will remain
                  saved to your account.
                </div>
                <div className="settings-drawer-content__login-view__button-container">
                  <button
                    onClick={onOpenSignup}
                    className="settings-drawer-content__login-view__button-container--signup-button"
                  >
                    Sign Up
                  </button>
                  <button
                    onClick={onOpenLogin}
                    className="settings-drawer-content__login-view__button-container--login-button"
                  >
                    Log In
                  </button>
                </div>
              </div>
            </div>
          ) : !hasAccess ? (
            <div className="settings-drawer-content__body">
              <div className="settings-drawer-content__login-view">
                <div className="settings-drawer-content__login-view--login-message">
                  You can create and save your customized dashboards here. Subscribe to BetQL Sharp to save unlimited
                  dashboards to your account.
                </div>
                <div className="settings-drawer-content__login-view__button-container">
                  <button
                    onClick={() => {
                      ctx.redirectToPricing(
                        {
                          screen: "FeedView",
                          page: "Games Table",
                          source: "Save Dashboard",
                        },
                        "Games Table Save Dashboard"
                      );
                      closeDrawer();
                    }}
                    className="settings-drawer-content__login-view__button-container--signup-button"
                  >
                    Sharp Access
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="settings-drawer-content__content-view">
              <div className="games-view__dashboard-setting">
                <div className="games-view__dashboard-setting-icon-label">
                  <input
                    className="games-view__dashboard-setting-dashboard-label"
                    value={currentDashName}
                    onChange={onChangeDashName}
                    onBlur={onBlurDashName}
                    onFocus={onFocusDashName}
                    maxLength={40}
                  />
                </div>
                <div className="games-view__dashboard-setting-controls">
                  <button
                    className="games-view__dashboard-setting-save-btn"
                    onClick={hasAccess ? onSaveSetting : toggleDrawer}
                    disabled={Boolean(!currentDashName || isDefaultDashName)}
                  >
                    Save New
                  </button>
                  {userColumnOrderSetting.updateTs ? (
                    <button className="games-view__dashboard-setting-update-btn" onClick={onUpdateSettings}>
                      Update
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="settings-drawer-content__content-view--strip-headline">
                <span>Name</span>
                <span>
                  <span className="settings-drawer-content__content-view--strip-headline--date">
                    <Icon
                      className="settings-drawer-content__content-view--strip-headline--date--icon"
                      path={mdiMenuUp}
                      size="25px"
                      color="#888888"
                    />
                    Date Saved
                  </span>
                  <Icon
                    className="settings-drawer-content__content-view--strip-headline--delete-icon"
                    path={mdiTrashCanOutline}
                    size="25px"
                    color="#FF4F4D"
                  />
                </span>
              </div>
              {settingList.length <= 2 ? (
                <div className="settings-drawer-content__content-view--no-setting-message">
                  You can create and save your customized dashboards here. As a BetQL member these views will remain
                  saved to your account.
                  <div className="settings-drawer-content__mobile-message d-sm-none">
                    Currently this feature is available on Tablet and Desktop version of the app!
                  </div>
                </div>
              ) : null}
              <div className="settings-drawer-content__content-view--create-setting-row">
                {settingList.map((setting: ColumnOrderSetting) => {
                  const settingValueObject = setting.parsedValue;
                  return (
                    <React.Fragment key={setting.id}>
                      <div className="settings-drawer-content__content-view--create-setting-row--setting-list">
                        <div className="settings-drawer-content__content-view--create-setting-row--start-content">
                          <button
                            className="settings-drawer-content__content-view--create-setting-row--setting-name-btn"
                            onClick={() => onSelectSetting(setting)}
                          >
                            <span className="settings-drawer-content__content-view--create-setting-row--setting-name">
                              {settingValueObject.name}
                            </span>
                          </button>
                        </div>
                        {/* This is a temp hack to not let user delete Default Dashboard, ideally we should check default id but
                          currently we dont have a user_ui_settings_default table populated with new default content. */}
                        {setting.updateTs ? (
                          <div className="settings-drawer-content__content-view--create-setting-row--end-content">
                            <span className="settings-drawer-content__content-view--create-setting-row--end-content--date">
                              {moment(setting.updateTs).format("MM/DD/YY")}
                            </span>
                            <div
                              className="settings-drawer-content__content-view--create-setting-row--end-content--delete-icon lazyload"
                              onClick={() => onDeleteSetting(setting.id)}
                            >
                              <Icon path={mdiTrashCanOutline} size="25px" color="#FF4F4D" />
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div className="settings-drawer-content__content-view--horizontal-line" />
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <style jsx global>
          {styles}
        </style>
      </RCDrawer>
    );
  }
);

export default Drawer;
